import {
  AnalysisStore,
  LegendItem,
  GroupLabel,
  Raster,
  Polygon,
  PointChecked,
  LineAnalysisItem
} from "helpers/models";
import { getLegendDisplayOpacity } from "./legend-display";
import * as turf from "@turf/turf";

/* ======================= 常量 ======================= */
const FATHOM_LAYERS = new Map([
  ["FL_Fluvial_Global_Fathom", 0],
  ["FL_Coastal_Global_Fathom", 1]
]);

const SPECIAL_LAYERS = new Set([
  "DR_Subsidence_France_SwissRe",
  "WF_DistToBush_AUS_SwissRe"
]);

export const EXTREME_KEY_LAYER_RESULT: any = {
  FL_Fluvial_Global_Fathom: {
    key: "depth",
    type: "number"
  },
  FL_Pluvial_Global_Fathom: {
    key: "depth",
    type: "number"
  },
  FL_Coastal_Global_Fathom: {
    key: "depth",
    type: "number"
  },
  FL_Fluvial_Official: {
    key: "FF Return Period (Official)",
    type: "legend"
  },
  FL_Fluvial_SwissRe: {
    key: "FF Return Period (SR)",
    type: "legend"
  },
  FL_Pluvial_SwissRe: {
    key: "PF Return Period (SR)",
    type: "legend"
  },
  FL_Surge_SwissRe: {
    key: "Surge Return Period",
    type: "legend"
  },
  EQ_Tsunami_SwissRe: {
    key: "Wave Height (m)",
    type: "number"
  },
  EQ_Bedrock_Global_SwissRe: {
    key: "EQ PGA [g]",
    type: "number"
  },
  EQ_LocalSoilCondition_Global_SwissRe: {
    key: "EQ PGA [g]",
    type: "number"
  },
  WS_Windspeed_Global_SwissRe: {
    key: "Storm Peak Gust [m/s]",
    type: "number"
  },
  CS_Hail_Global_SwissRe: {
    key: "Hail Days (>2.5cm) per Year",
    type: "number"
  },
  CS_Tornado_Global_SwissRe: {
    key: "Tornado Days (Tornado ≥ EF1) per Year",
    type: "number"
  },
  EQ_Landslide_Global_SwissRe: {
    key: "Landslide Hazard",
    type: "legend"
  },
  CS_Lightning_Global_SwissRe: {
    key: "Lightning Days (≥ 1 detected event) per Year",
    type: "number"
  },
  VO_AshThickness_Global_SwissRe: {
    key: "Volcano Ash Thickness [cm]",
    type: "number"
  },
  WF_Wildfire_Global_SwissRe: {
    key: "Wildfire Susceptibility",
    type: "number"
  }
};

const EXTREME_LAYER_COLOR: any = {
  "FL_Fluvial_Global_Fathom": { r: "214", g: "212", b: "145" },
  "FL_Pluvial_Global_Fathom": { r: "255", g: "247", b: "209" },
  "FL_Coastal_Global_Fathom": { r: "128", g: "0", b: "128" },
  "FL_Fluvial_Official": { r: "6", g: "69", b: "141" },
  "FL_Fluvial_SwissRe": { r: "27", g: "145", b: "222" },
  "FL_Pluvial_SwissRe": { r: "64", g: "71", b: "224" },
  "FL_Surge_SwissRe": { r: "64", g: "26", b: "123" },
  "EQ_Tsunami_SwissRe": { r: "20", g: "210", b: "209" },
  "EQ_Bedrock_Global_SwissRe": { r: "235", g: "2", b: "0" },
  "EQ_LocalSoilCondition_Global_SwissRe": { r: "204", g: "0", b: "153" },
  "WS_Windspeed_Global_SwissRe": { r: "20", g: "210", b: "209" },
  "CS_Hail_Global_SwissRe": { r: "254", g: "143", b: "144" },
  "CS_Tornado_Global_SwissRe": { r: "60", g: "65", b: "80" },
  "EQ_Landslide_Global_SwissRe": { r: "135", g: "32", b: "0" },
  "CS_Lightning_Global_SwissRe": { r: "241", g: "181", b: "53" },
  "VO_AshThickness_Global_SwissRe": { r: "219", g: "125", b: "96" },
  "WF_Wildfire_Global_SwissRe": { r: "208", g: "2", b: "27" }
}

/* ======================= 工具函数 ======================= */
export function isFathomLayer(layerId: string) {
  return FATHOM_LAYERS.has(layerId);
}

function getNoDataStyle(label: string) {
  if (label === "Outside" || label === "No Observation") {
    return { color: "#f2f2f2", opacity: 0.5 };
  }
  return { color: "grey", opacity: 0.5 };
}

function sortBySortIndex<T extends { sortIndex?: number }>(list: T[]) {
  return list.slice().sort((a, b) => {
    const ai = a.sortIndex ?? 0;
    const bi = b.sortIndex ?? 0;
    return bi - ai;
  });
}

function chartSortBySortIndex<T extends { sortIndex?: number }>(list: T[]) {
  return list.slice().sort((a, b) => {
    const ai = a.sortIndex ?? 0;
    const bi = b.sortIndex ?? 0;
    return bi - ai;
  });
}

/* ======================= label / sortIndex 规则 ======================= */
function normalizeYearLabel(label: string, layerId: string) {
  let sortIndex = undefined;
  let newLabel = label;
  if (layerId.includes("Fathom")) {
    if (["5 years", "10 years", "20 years", "50 years"].includes(label)) {
      if (label === "5 years") {
        sortIndex = 9;
      } else if (label === "10 years") {
        sortIndex = 8;
      } else if (label === "20 years") {
        sortIndex = 7;
      }
      newLabel = "≤50 years";
    } else if (label === "1000 years") {
      newLabel = "≥1000 years";
    }
  } else if (layerId === "FL_Fluvial_Official" && label === "> 500 years") {
    newLabel = "> 500 years (US)";
  } else if (label === "Negligible (< 0.014)" || label === "Negligible") {
    sortIndex = 0.5;
  }

  return { label: newLabel, sortIndex };
}

/* ======================= ruleMap ======================= */
type RuleStyle = {
  color: string;
  opacity: number;
  sortIndex: number;
};

function buildRuleMap(legendItem: LegendItem): Map<string, RuleStyle> {
  const map = new Map<string, RuleStyle>();

  legendItem.rules.forEach((rule: any, index: number) => {
    const symbol = rule?.symbolizers?.[0];

    const color =
      (symbol as Raster)?.raster?.color ??
      (symbol as Polygon)?.polygon?.fill ??
      "";

    const rawOpacity =
      (symbol as Raster)?.raster?.opacity ??
      (symbol as Polygon)?.polygon?.fillOpacity ??
      0;
    const opacity = getLegendDisplayOpacity(
      legendItem.layerId,
      rule.title,
      rawOpacity
    ) ?? rawOpacity;

    map.set(rule.title, {
      color,
      opacity,
      sortIndex: index
    });
  });

  return map;
}

function getLegendMap(legendData: LegendItem[]) {
  let legendMap = new Map();
  legendData.forEach((item: any, index: number) => {
    if (!legendMap.has(item.layerId)) {
      legendMap.set(item.layerId, {
        ...item,
        index,
        ruleMap: buildRuleMap(item)
      });
    }
  });
  return legendMap;
}

/* ======================= Fathom Defended ======================= */
function getDefendedValueLabel(resultItem: any, key = "Defended"): string {
  try {
    const data = resultItem.details?.[key]
      ? JSON.parse(resultItem.details[key])
      : [];

    const floodItem = data.find((i: any) => parseFloat(i.depth) > 0);

    return (
      (floodItem?.returnPeriod
        ? `${floodItem.returnPeriod} years`
        : data?.[0]?.valueLabel) ?? "No Data"
    );
  } catch {
    return "No Data";
  }
}

export function getFathom1000YearsDepth(resultItem: any, key = "Undefended"): string {
  try {
    const data = resultItem.details?.[key]
      ? JSON.parse(resultItem.details[key])
      : [];

    const floodItem = data[data.length - 1];
    const depth = parseFloat(floodItem?.depth);
    if (isNaN(depth)) {
      return data?.[0]?.valueLabel ?? "No Data";
    }
    return floodItem?.depth ?? "No Data";
  } catch {
    return "No Data";
  }
}

/* ======================= 单条数据收集 ======================= */
function collectOne(
  stats: Record<string, GroupLabel>,
  rawLabel: string,
  resultItem: any,
  tableItem: any,
  layerId: string,
  ruleMap: Map<string, RuleStyle>,
  legendItem: LegendItem
) {
  let sortIndex = undefined,
    itemStyle: any = {};
  if (
    rawLabel === "No Data" ||
    rawLabel === "No_Data" ||
    rawLabel === "Outside" ||
    rawLabel === "No Observation"
  ) {
    itemStyle = getNoDataStyle(rawLabel);
    sortIndex = -1;
    stats[rawLabel] ??= {
      subtitle: legendItem.subtitle,
      title: legendItem.title,
      layerId,
      label: rawLabel,
      pointIds: [],
      ...itemStyle,
      itemStyle,
      sortIndex: -1
    };
  } else {
    const normalized = normalizeYearLabel(rawLabel, layerId);
    const rule = ruleMap.get(normalized.label);

    if (!rule) return;
    sortIndex = normalized.sortIndex ?? rule.sortIndex;
    itemStyle = {
      color: rule.color,
      opacity: rule.opacity
    };
    stats[rawLabel] ??= {
      subtitle: legendItem.subtitle,
      title: legendItem.title,
      layerId,
      label: rawLabel,
      pointIds: [],
      ...itemStyle,
      itemStyle,
      sortIndex: sortIndex
    };
  }

  stats[rawLabel].pointIds.push({
    subtitle: legendItem.subtitle,
    title: legendItem.title,
    ...tableItem,
    ...resultItem,
    id: resultItem.pointId,
    sortIndex,
    itemStyle,
    ...itemStyle
  });
}

/* ======================= 一次 locations 循环 ======================= */
function collectStatsOnce({
  layerId,
  layerMap,
  locations,
  ruleMap,
  legendItem,
  pdfResult
}: {
  layerId: string;
  layerMap: Map<string, any>;
  locations: any[];
  ruleMap: Map<string, RuleStyle>;
  legendItem: LegendItem;
  pdfResult?: any;
}) {
  const normalStats: Record<string, GroupLabel> = {};
  const defendedStats: Record<string, GroupLabel> = {};
  const noAnalysisPoints: any[] = [];

  locations.forEach((tableItem) => {
    const pointId = tableItem.id.replaceAll("-", "");
    const resultItem = layerMap.get(pointId);

    if (!resultItem) {
      tableItem.checked && noAnalysisPoints.push(tableItem);
      return;
    }
    pdfResult.push({ ...resultItem });
    collectOne(
      normalStats,
      resultItem.valueLabel ?? "No Data",
      resultItem,
      tableItem,
      layerId,
      ruleMap,
      legendItem
    );

    if (isFathomLayer(layerId)) {
      collectOne(
        defendedStats,
        getDefendedValueLabel(resultItem),
        resultItem,
        tableItem,
        layerId,
        ruleMap,
        legendItem
      );
    }
  });

  return {
    normal: Object.values(normalStats),
    defended: Object.values(defendedStats),
    noAnalysisPoints
  };
}

/////一次统计数据
export const countData = (data: GroupLabel[], unit: string) => {
  let rowCount = 0, ////checked为true的点数量
    totalCount = 0, ////所有点数量
    rowPointIds: string[] = [], ////checked为true点的id
    totalPointIds: string[] = [], //////所有点的id
    unitCount = 0, /////checked为true的unit
    unitTotalCount = 0; /////所有点的unit
  data?.forEach((i) => {
    totalCount += i.pointIds.length;
    rowCount += i.pointIds.filter((point) => {
      totalPointIds.push(point.id.split("-").join(""));
      unitTotalCount += Math.round(point[unit]);

      if (point.checked) {
        unitCount += Math.round(point[unit]);
        rowPointIds.push(point.id.split("-").join(""));
      }

      return point.checked;
    }).length;
  });
  return {
    rowCount,
    totalCount,
    rowPointIds,
    totalPointIds,
    unitCount,
    unitTotalCount
  };
};

//统计data的pointIds的checked为true的数量
export const countTotalCheckedPointIds = (data: GroupLabel[]): number => {
  return data?.reduce((count, group) => {
    const checkedCount = group.pointIds.filter((point) => point.checked).length;
    return count + checkedCount;
  }, 0);
};

export const countFathomTotalCheckedPointIds = (data: any): number => {
  if (data.undefended) {
    return countTotalCheckedPointIds(data.undefended);
  }
  return countTotalCheckedPointIds(data);
};

/* ======================= 主函数 ======================= */
////地点集自然灾害和event分析结果组装
export const handleGetMultipleResultGroupData = ({
  storeData,
  locationsTableData,
  legendData,
  needSort = false,
  needChartData = true,
  needNoData = true
}: {
  storeData: AnalysisStore;
  locationsTableData: any[];
  legendData: LegendItem[];
  needSort?: boolean;
  needChartData?: boolean;
  needNoData?: boolean;
}) => {
  const result: GroupLabel[][] = [];
  const fathomDefendeResult: GroupLabel[][] = [];
  const pdfResult: any = [];
  const noData: GroupLabel[] = [];
  const noAnalysis: any[] = [];
  const chartData: PointChecked[][] = [];
  legendData.forEach((legendItem, index) => {
    const layerMap = storeData.get(legendItem.layerId);
    if (!layerMap) return;

    const ruleMap = buildRuleMap(legendItem);

    const { normal, defended, noAnalysisPoints } = collectStatsOnce({
      layerId: legendItem.layerId,
      layerMap,
      locations: locationsTableData,
      ruleMap,
      legendItem,
      pdfResult
    });

    const finalNormal = needSort ? sortBySortIndex(normal) : normal;

    // noAnalysis：只在第一个 legend
    if (index === 0 && noAnalysisPoints.length) {
      noAnalysis.push(...noAnalysisPoints);
    }

    const isFathom = isFathomLayer(legendItem.layerId);

    if (needNoData) {
      if (isFathom) {
        const finalDefended = needSort ? sortBySortIndex(defended) : defended;

        /* ========== Fathom 图层 ========== */
        const normalIsNoData =
          finalNormal.length === 1 &&
          (finalNormal[0].label === "No Data" ||
            finalNormal[0].label === "No_Data");

        const defendedIsNoData =
          finalDefended.length === 1 &&
          (finalDefended[0].label === "No Data" ||
            finalDefended[0].label === "No_Data");

        if (normalIsNoData && defendedIsNoData) {
          noData.push(finalNormal[0]);
        } else {
          result.push(finalNormal);
          fathomDefendeResult.push(finalDefended);

          //  chart 数据：先“选 + 拷贝”，再排序，最后合并 pointIds
          if (needChartData && finalNormal.length) {
            //  合并 pointIds
            const mergedPointIds: any = finalNormal.flatMap(
              (group) => group.pointIds
            );

            // 使用公共工具排序（point.sortIndex）
            const sortedPointIds: any = chartSortBySortIndex(mergedPointIds);

            chartData.push(sortedPointIds);
          }
        }
      } else {
        /* ========== 非 Fathom 图层 ========== */
        const isSingle = finalNormal.length === 1;
        const label = finalNormal[0]?.label;
        const isSpecialLayer = SPECIAL_LAYERS.has(legendItem.layerId);

        const isNoDataCase =
          isSingle &&
          (label === "No Data" ||
            label === "No_Data" ||
            (isSpecialLayer &&
              (label === "Outside" ||
                label === "No Observation" ||
                label === "Negligible")));

        if (isNoDataCase) {
          noData.push(finalNormal[0]);
        } else {
          result.push(finalNormal);
          //  chart 数据：先“选 + 拷贝”，再排序，最后合并 pointIds
          if (needChartData && finalNormal.length) {
            //  合并 pointIds
            const mergedPointIds: any = finalNormal.flatMap(
              (group) => group.pointIds
            );

            // 使用公共工具排序（point.sortIndex）
            const sortedPointIds: any = chartSortBySortIndex(mergedPointIds);

            chartData.push(sortedPointIds);
          }
        }
      }
    } else {
      result.push(finalNormal);
      //  chart 数据：先“选 + 拷贝”，再排序，最后合并 pointIds
      if (needChartData && finalNormal.length) {
        //  合并 pointIds
        const mergedPointIds: any = finalNormal.flatMap(
          (group) => group.pointIds
        );

        // 使用公共工具排序（point.sortIndex）
        const sortedPointIds: any = chartSortBySortIndex(mergedPointIds);

        chartData.push(sortedPointIds);
      }
    }
  });

  return {
    result,
    fathomDefendeResult,
    pdfResult,
    noData,
    noAnalysis,
    chartData
  };
};

function ExtremeCollect(
  extremeMap: Map<string, any>,
  resultItem: any,
  legendItem: LegendItem
) {
  const appendSameValueItem = (type: "max" | "min") => {
    const layerExtreme = extremeMap.get(resultItem.layerId);
    if (!layerExtreme) {
      return;
    }

    const listKey = type === "max" ? "maxSameValueItems" : "minSameValueItems";
    layerExtreme[listKey].push({ ...resultItem });
  };

  if (
    resultItem.valueLabel !== "No Data" &&
    resultItem.valueLabel !== "No_Data" &&
    resultItem.valueLabel !== "Outside" &&
    resultItem.valueLabel !== "No Observation" &&
    resultItem.valueLabel !== "Waterbody"
  ) {
    if (!extremeMap.has(resultItem.layerId)) {
      extremeMap.set(resultItem.layerId, {
        max: { ...resultItem },
        min: { ...resultItem },
        maxSameValueItems: [{ ...resultItem }],
        minSameValueItems: [{ ...resultItem }]
      });
    } else {
      const layerExtreme = extremeMap.get(resultItem.layerId);
      const currentMax = layerExtreme.max;
      const currentMin = layerExtreme.min;
      if (resultItem.layerId.includes("Fathom")) {
        const depth = getFathom1000YearsDepth(resultItem, isFathomLayer(resultItem.layerId) ? "Undefended" : "Defended");
        const depthValue = parseFloat(depth);

        const currentMaxDepthValue = parseFloat(
          getFathom1000YearsDepth(currentMax, isFathomLayer(currentMax.layerId) ? "Undefended" : "Defended")
        );

        const currentMinDepthValue = parseFloat(
          getFathom1000YearsDepth(currentMin, isFathomLayer(currentMin.layerId) ? "Undefended" : "Defended")
        );

        if (!isNaN(depthValue)) {
          if (depthValue > currentMaxDepthValue) {
            layerExtreme.max = { ...resultItem };
            layerExtreme.maxSameValueItems = [{ ...resultItem }];
          } else if (depthValue === currentMaxDepthValue) {
            appendSameValueItem("max");
          }
          if (depthValue < currentMinDepthValue) {
            layerExtreme.min = { ...resultItem };
            layerExtreme.minSameValueItems = [{ ...resultItem }];
          } else if (depthValue === currentMinDepthValue) {
            appendSameValueItem("min");
          }
        }
      } else {
        const extremeKey = EXTREME_KEY_LAYER_RESULT[resultItem.layerId];
        const extremeValue = resultItem.details[extremeKey.key];
        const currentMaxExtremeValue = currentMax.details[extremeKey.key];
        const currentMinExtremeValue = currentMin.details[extremeKey.key];

        if (extremeKey.type === "number") {
          if (parseFloat(extremeValue) > parseFloat(currentMaxExtremeValue)) {
            layerExtreme.max = { ...resultItem };
            layerExtreme.maxSameValueItems = [{ ...resultItem }];
          } else if (
            parseFloat(extremeValue) === parseFloat(currentMaxExtremeValue)
          ) {
            appendSameValueItem("max");
          }
          if (parseFloat(extremeValue) < parseFloat(currentMinExtremeValue)) {
            layerExtreme.min = { ...resultItem };
            layerExtreme.minSameValueItems = [{ ...resultItem }];
          } else if (
            parseFloat(extremeValue) === parseFloat(currentMinExtremeValue)
          ) {
            appendSameValueItem("min");
          }
        } else {
          const normalized = normalizeYearLabel(
            extremeValue,
            resultItem.layerId
          );
          const rule = legendItem.ruleMap.get(normalized.label);
          const sortIndex = normalized.sortIndex ?? rule?.sortIndex ?? 0;

          const currentMaxNormalized = normalizeYearLabel(
            currentMaxExtremeValue,
            resultItem.layerId
          );
          const currentMaxRule = legendItem.ruleMap.get(
            currentMaxNormalized.label
          );
          const currentMaxSortIndex =
            currentMaxNormalized.sortIndex ?? currentMaxRule?.sortIndex ?? 0;
          const currentMinNormalized = normalizeYearLabel(
            currentMinExtremeValue,
            resultItem.layerId
          );
          const currentMinRule = legendItem.ruleMap.get(
            currentMinNormalized.label
          );
          const currentMinSortIndex =
            currentMinNormalized.sortIndex ?? currentMinRule?.sortIndex ?? 0;

          if (sortIndex > currentMaxSortIndex) {
            layerExtreme.max = { ...resultItem };
            layerExtreme.maxSameValueItems = [{ ...resultItem }];
          } else if (sortIndex === currentMaxSortIndex) {
            appendSameValueItem("max");
          }
          if (sortIndex < currentMinSortIndex) {
            layerExtreme.min = { ...resultItem };
            layerExtreme.minSameValueItems = [{ ...resultItem }];
          } else if (sortIndex === currentMinSortIndex) {
            appendSameValueItem("min");
          }
        }
      }
    }
  }
}

function DistanceCollect(distanceMap: Map<string, any>, resultItem: any) {
  let intensity = 0;
  if (resultItem.layerId.includes("Fathom")) {
    intensity = resultItem.intensity * 1
  } else {
    intensity = resultItem.intensity === 0 ? 1 : resultItem.intensity;
  }
  if (distanceMap.has(resultItem.layerId)) {
    distanceMap.get(resultItem.layerId).data[resultItem.index] = {
      ...resultItem,
      value: intensity
    };
  } else {
    const baseColor =
      EXTREME_LAYER_COLOR[resultItem.layerId] ??
      { r: "120", g: "120", b: "120" };
    const { r, g, b } = baseColor;
    let data = [];
    data[resultItem.index] = {
      ...resultItem,
      value: intensity
    };
    distanceMap.set(resultItem.layerId, {
      name: resultItem.layerId,
      type: "line",
      smooth: true,
      data: data,
      progressive: 0,
      progressiveThreshold: 0,
      sampling: "none",
      // showSymbol: false,
      showAllSymbol: true,
      legendHoverLink: false,
      symbol: "circle",
      symbolSize: 10,
      itemStyle: {
        opacity: 0,
        color: `rgba(${r}, ${g}, ${b}, 1)`,
        borderWidth: 0
      },
      lineStyle: {
        width: 2,
        color: `rgba(${r}, ${g}, ${b}, 1)`
      },
      emphasis: {
        disabled: false,
        focus: "series",
        lineStyle: {
          width: 3
        },
        itemStyle: {
          opacity: 1,
          color: `rgba(${r}, ${g}, ${b}, 1)`,
          borderColor: `rgba(255, 255, 255, 1)`,
          borderWidth: 1,
          shadowColor: `rgba(${r}, ${g}, ${b}, 1)`,
          shadowBlur: 10
        }
      }
    });
  }
}

function calculateDistanceByLngLatMeters(
  start: [number, number],
  end: [number, number]
) {
  const startPoint = turf.point(start);
  const endPoint = turf.point(end);
  return turf.distance(startPoint, endPoint, { units: "meters" });
}

function attachExtremeDistanceFromStart(extremeItem: any, distanceItem: any) {
  const startLongitude = Number(distanceItem?.data?.[0]?.longitude);
  const startLatitude = Number(distanceItem?.data?.[0]?.latitude);

  if (!Number.isFinite(startLongitude) || !Number.isFinite(startLatitude)) {
    return;
  }

  const startPoint: [number, number] = [startLongitude, startLatitude];

  const maxLongitude = Number(extremeItem.max?.longitude);
  const maxLatitude = Number(extremeItem.max?.latitude);
  const minLongitude = Number(extremeItem.min?.longitude);
  const minLatitude = Number(extremeItem.min?.latitude);

  const maxDistanceFromStartMeters = calculateDistanceByLngLatMeters(startPoint, [maxLongitude, maxLatitude]);
  const minDistanceFromStartMeters = calculateDistanceByLngLatMeters(startPoint, [minLongitude, minLatitude]);

  if (maxDistanceFromStartMeters !== undefined) {
    extremeItem.max.distanceFromStartMeters = maxDistanceFromStartMeters;
    extremeItem.maxDistanceFromStartMeters = maxDistanceFromStartMeters;
  }

  if (minDistanceFromStartMeters !== undefined) {
    extremeItem.min.distanceFromStartMeters = minDistanceFromStartMeters;
    extremeItem.minDistanceFromStartMeters = minDistanceFromStartMeters;
  }
}

////线性分析结果组装
export const handleGetLineResultGroupData = ({
  resultData,
  legendData,
  needSort = false,
  needChartData = true
}: {
  resultData: LineAnalysisItem[];
  legendData: LegendItem[];
  needSort?: boolean;
  needChartData?: boolean;
}) => {
  const result: GroupLabel[][] = [];
  const fathomDefendeResult: GroupLabel[][] = [];
  const noData: GroupLabel[] = [];
  const chartData: PointChecked[][] = [];
  const distanceData: any = [];

  const legendMap = getLegendMap(legendData);

  const resultMap: Map<string, any> = new Map();
  const fathomDefendeMap: Map<string, any> = new Map();
  const extremeMap: Map<string, any> = new Map();
  const distanceMap: Map<string, any> = new Map();

  resultData.forEach((resultItem) => {
    resultItem.id = String(resultItem.index);
    resultItem.pointId = String(resultItem.index);
    resultItem.checked = true;
    let legendItem = legendMap.get(resultItem.layerId);
    let ruleMap = legendItem.ruleMap;

    ExtremeCollect(extremeMap, resultItem, legendItem);

    DistanceCollect(distanceMap, resultItem);

    if (!resultMap.has(resultItem.layerId)) {
      resultMap.set(resultItem.layerId, {});
    }
    collectOne(
      resultMap.get(resultItem.layerId),
      resultItem.valueLabel ?? "No Data",
      resultItem,
      undefined,
      resultItem.layerId,
      ruleMap,
      legendItem
    );

    if (isFathomLayer(resultItem.layerId)) {
      if (!fathomDefendeMap.has(resultItem.layerId)) {
        fathomDefendeMap.set(resultItem.layerId, {});
      }
      collectOne(
        fathomDefendeMap.get(resultItem.layerId),
        getDefendedValueLabel(resultItem),
        resultItem,
        undefined,
        resultItem.layerId,
        ruleMap,
        legendItem
      );
    }
  });

  legendData.forEach((legendItem) => {
    const normal: any = Object.values(resultMap.get(legendItem.layerId));
    const finalNormal: any = needSort ? sortBySortIndex(normal) : normal;
    const isFathom = isFathomLayer(legendItem.layerId);
    const distanceItem = distanceMap.get(legendItem.layerId);
    let extremeItem = extremeMap.get(legendItem.layerId);

    if (extremeItem) {
      attachExtremeDistanceFromStart(extremeItem, distanceItem);
    }

    if (isFathom) {
      const defended: any = Object.values(
        fathomDefendeMap.get(legendItem.layerId)
      );
      const finalDefended: any = needSort
        ? sortBySortIndex(defended)
        : defended;

      /* ========== Fathom 图层 ========== */
      const normalIsNoData =
        finalNormal.length === 1 &&
        (finalNormal[0].label === "No Data" ||
          finalNormal[0].label === "No_Data");

      const defendedIsNoData =
        finalDefended.length === 1 &&
        (finalDefended[0].label === "No Data" ||
          finalDefended[0].label === "No_Data");

      if (normalIsNoData && defendedIsNoData) {
        noData.push(finalNormal[0]);
      } else {
        result.push(finalNormal);
        fathomDefendeResult.push(finalDefended);
        distanceData.push(distanceItem);

        //  chart 数据：先“选 + 拷贝”，再排序，最后合并 pointIds
        if (needChartData && finalNormal.length) {
          //  合并 pointIds
          const mergedPointIds: any = finalNormal.flatMap(
            (group: any) => group.pointIds
          );

          // 使用公共工具排序（point.sortIndex）
          const sortedPointIds: any = chartSortBySortIndex(mergedPointIds);

          chartData.push(sortedPointIds);
        }
      }
    } else {
      /* ========== 非 Fathom 图层 ========== */
      const isSingle = finalNormal.length === 1;
      const label = finalNormal[0]?.label;

      const isNoDataCase =
        isSingle && (label === "No Data" || label === "No_Data");

      if (isNoDataCase) {
        noData.push(finalNormal[0]);
      } else {
        result.push(finalNormal);
        distanceData.push(distanceItem);
        //  chart 数据：先“选 + 拷贝”，再排序，最后合并 pointIds
        if (needChartData && finalNormal.length) {
          //  合并 pointIds
          const mergedPointIds: any = finalNormal.flatMap(
            (group: any) => group.pointIds
          );

          // 使用公共工具排序（point.sortIndex）
          const sortedPointIds: any = chartSortBySortIndex(mergedPointIds);

          chartData.push(sortedPointIds);
        }
      }
    }
  });
  
  return {
    result,
    fathomDefendeResult,
    noData,
    chartData,
    extremeMap,
    distanceData
  };
};
